import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon } from './icon';

export interface Crumb {
  label: string;
  to?: string;
}

/** Top of every page: breadcrumbs or back link, title, actions. */
export function PageHeader({
  title,
  crumbs,
  back,
  actions,
  children,
}: {
  title: string;
  crumbs?: readonly Crumb[];
  /** "← Back to …" link (FR-NAV-007). */
  back?: { to: string; label: string };
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-2">
      {back && (
        <Link to={back.to} className="flex w-fit items-center gap-1.5 text-sm text-brand-700 hover:underline">
          <Icon name="arrow-left" />
          {back.label}
        </Link>
      )}
      {crumbs && crumbs.length > 0 && (
        <nav aria-label="breadcrumb" className="text-sm text-ink-muted">
          <ol className="flex flex-wrap items-center gap-1.5">
            {crumbs.map((c, i) => (
              <li key={`${c.label}-${i}`} className="flex items-center gap-1.5">
                {i > 0 && <span aria-hidden>/</span>}
                {c.to ? (
                  <Link to={c.to} className="hover:text-ink hover:underline">
                    {c.label}
                  </Link>
                ) : (
                  <span>{c.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}
